import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ListChecks, User, Shirt, FileSpreadsheet } from "lucide-react";

const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const gkey = (g) => `${g.kind}-${g.name}-${g.squad_ref}`;

const downloadBlob = (res, fallback) => {
  const cd = res.headers?.["content-disposition"] || "";
  let filename = fallback;
  const m = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (m) { try { filename = decodeURIComponent(m[1]); } catch {} }
  else { const m2 = /filename="?([^";]+)"?/i.exec(cd); if (m2) filename = m2[1]; }
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

export const OutstandingItems = ({ open, onOpenChange, isAdmin, onExported }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const fetchData = () => api.get("/kit/orders/outstanding").then(r => setData(r.data)).catch(() => setData({ groups: [], total_lines: 0, orders: 0 }));
  useEffect(() => { if (!open) return; setData(null); setPicked(new Set()); fetchData(); }, [open]);

  const groups = (data?.groups || []).filter(g => !q || g.name.toLowerCase().includes(q.toLowerCase()) || g.squad_ref.toLowerCase().includes(q.toLowerCase()));

  const pickedOrders = useMemo(() => {
    const refs = new Map();
    (data?.groups || []).filter(g => picked.has(gkey(g))).forEach(g => g.lines.forEach(l => refs.set(l.order_id, l.order_ref)));
    return refs;
  }, [data, picked]);

  const togglePick = (g) => setPicked(prev => { const n = new Set(prev); const k = gkey(g); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleAll = () => setPicked(picked.size === groups.length ? new Set() : new Set(groups.map(gkey)));

  const markOrdered = async () => {
    const ids = Array.from(pickedOrders.keys());
    if (!ids.length) return;
    const affected = (data?.groups || []).filter(g => !picked.has(gkey(g)) && g.lines.some(l => pickedOrders.has(l.order_id)));
    const warn = affected.length ? `\n\nNote: shop orders are per order, so these will also be included: ${affected.map(g => g.name).join(", ")}.` : "";
    if (!window.confirm(`Generate shop order for ${ids.length} order(s) (${Array.from(pickedOrders.values()).join(", ")})?\n\nThey will move to "Ordered" and coaches will be emailed.${warn}`)) return;
    setExporting(true);
    try {
      const res = await api.post("/admin/kit/orders/export", { order_ids: ids, advance_to_ordered: true }, { responseType: "blob" });
      const ref = res.headers?.["x-export-ref"] || "PK";
      downloadBlob(res, `EYFC Prokit - ${ref}.xlsx`);
      toast.success(`Shop order ${ref} generated`);
      setPicked(new Set());
      await fetchData();
      onExported?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  const downloadCsv = () => {
    const rows = [["Who", "Name", "Number/Initials", "Team", "Order", "Product", "Size", "Colour", "Qty", "Order status"]];
    groups.forEach(g => g.lines.forEach(l => rows.push([g.kind, g.name, l.squad_ref, l.team_name || "", l.order_ref, l.item_name, l.size, l.color, l.quantity, l.order_status])));
    const blob = new Blob([rows.map(r => r.map(esc).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "kit-outstanding-items.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="kit-outstanding-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-widest uppercase flex items-center gap-2"><ListChecks className="w-5 h-5 text-[#DFFF00]" /> Outstanding kit items</DialogTitle>
        </DialogHeader>
        <p className="text-sm opacity-60 -mt-2">Every item on orders not yet sent to the shop (Submitted / Approved), grouped by player or coach.{isAdmin ? " Tick groups to send them to a shop order." : ""}</p>
        <div className="flex flex-wrap gap-3 items-center">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or number…" className="max-w-xs" data-testid="kit-outstanding-search" />
          {data && <span className="text-xs tracking-widest uppercase opacity-60" data-testid="kit-outstanding-count">{data.total_lines} item{data.total_lines === 1 ? "" : "s"} · {data.orders} order{data.orders === 1 ? "" : "s"}</span>}
          <div className="ml-auto flex gap-2">
            {isAdmin && groups.length > 0 && <button onClick={toggleAll} className="text-xs tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B]" data-testid="kit-outstanding-select-all">{picked.size === groups.length ? "Clear" : "Select all"}</button>}
            {groups.length > 0 && <button onClick={downloadCsv} className="text-xs tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B]" data-testid="kit-outstanding-csv-btn">Download CSV</button>}
            {isAdmin && picked.size > 0 && (
              <button onClick={markOrdered} disabled={exporting} className="btn-volt text-xs tracking-widest uppercase px-3 py-1.5 rounded inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="kit-outstanding-mark-ordered">
                <FileSpreadsheet className="w-3.5 h-3.5" /> {exporting ? "Generating…" : `Mark as ordered (${pickedOrders.size} order${pickedOrders.size === 1 ? "" : "s"})`}
              </button>
            )}
          </div>
        </div>
        {!data && <div className="opacity-60 text-sm py-8 text-center">Loading…</div>}
        {data && groups.length === 0 && <div className="opacity-60 text-sm py-8 text-center" data-testid="kit-outstanding-empty">Nothing outstanding — every order has been sent to the shop.</div>}
        <div className="space-y-4">
          {groups.map(g => {
            const k = gkey(g);
            const on = picked.has(k);
            return (
              <div key={k} className={`card-tactical rounded-md p-4 ${on ? "ring-1 ring-[#DFFF00]/60" : ""}`} data-testid="kit-outstanding-group">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {isAdmin && <input type="checkbox" checked={on} onChange={() => togglePick(g)} className="accent-[#DFFF00] w-4 h-4" data-testid="kit-outstanding-pick" />}
                  {g.kind === "player" ? <Shirt className="w-4 h-4 text-[#DFFF00]" /> : <User className="w-4 h-4 text-[#83B68B]" />}
                  <span className="font-display tracking-wider uppercase text-sm">{g.name}</span>
                  {g.squad_ref && <span className="text-xs px-2 py-0.5 rounded bg-[#DFFF00]/10 text-[#DFFF00] font-mono">{g.squad_ref}</span>}
                  <span className="text-[10px] tracking-widest uppercase opacity-50">{g.kind}{g.team_name ? ` · ${g.team_name}` : ""}</span>
                  <span className="ml-auto text-xs opacity-60">{g.lines.length} item{g.lines.length === 1 ? "" : "s"}</span>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] tracking-widest uppercase opacity-50 text-left"><th className="pb-1 pr-3">Product</th><th className="pb-1 pr-3">Size</th><th className="pb-1 pr-3">Colour</th><th className="pb-1 pr-3">Qty</th><th className="pb-1 pr-3">Order</th><th className="pb-1">Status</th></tr></thead>
                  <tbody>
                    {g.lines.map((l, i) => (
                      <tr key={`${l.order_id}-${i}`} className="border-t border-border/40" data-testid="kit-outstanding-line">
                        <td className="py-1.5 pr-3">{l.item_name}{l.product_code ? <span className="opacity-40 text-xs ml-1">{l.product_code}</span> : null}</td>
                        <td className="py-1.5 pr-3">{l.size || "—"}</td>
                        <td className="py-1.5 pr-3">{l.color || "—"}</td>
                        <td className="py-1.5 pr-3">{l.quantity}</td>
                        <td className="py-1.5 pr-3 font-mono text-xs opacity-70">{l.order_ref}</td>
                        <td className="py-1.5 text-xs capitalize opacity-70">{l.order_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
